import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";

export interface MetadataTableEventArg
{
    /**
     * The type of entity backing the table, or if @SubProperyName is specified, the type of the parent model
     */
    Type: string;
    /**
     * Set this if you want to refresh all subproperty grids on the form
     */
    AllSubPropertyGrids: boolean;
    /**
     * If triggered from a recalculate, check these against grid sub property
     */
    PropertiesToRecalc: string[];
    /**
     * Force grid to refetch from server
    */
    Refetch?: boolean;
    ReloadColumns: boolean;
    ClearFilter: boolean;
    ApplyFilter: boolean;
}

export interface ProjectionGridEventArg
{
    ProviderId: string;
    Entities: any[];
}

export interface GridSelectionEventArg
{
    ProviderId: string;
    Clear: boolean;
}

@Injectable({ providedIn: "root" })
export class GridEventService
{
    private readonly updateProjectionGrid$ = new Subject<ProjectionGridEventArg>();

    get onUpdateProjectionGrid(): Observable<ProjectionGridEventArg>
    {
        return this.updateProjectionGrid$.asObservable();
    }

    fireUpdateProjectionGridListeners(value: ProjectionGridEventArg): void
    {
        this.updateProjectionGrid$.next(value);
    }

    private readonly loadMetadataTable$ = new Subject<MetadataTableEventArg>();

    get onLoadMetadataTable(): Observable<MetadataTableEventArg>
    {
        return this.loadMetadataTable$.asObservable();
    }

    fireLoadMetadataTableListeners(value: MetadataTableEventArg): void
    {
        this.loadMetadataTable$.next(value);
    }

    private readonly tableEntityChanged$ = new Subject<string>();

    get onTableEntityChanged(): Observable<string>
    {
        return this.tableEntityChanged$.asObservable();
    }

    fireTableEntitiesChangedListeners(providerId: string): void
    {
        this.tableEntityChanged$.next(providerId);
    }

    private readonly tableLoadingFinished$ = new Subject<string>();

    get onTableLoadingFinished(): Observable<string>
    {
        return this.tableLoadingFinished$.asObservable();
    }

    fireTableLoadingFinishedListeners(providerId: string): void
    {
        this.tableLoadingFinished$.next(providerId);
    }

    private readonly refreshGrid$ = new Subject<void>();

    get onRefreshGrid(): Observable<void>
    {
        return this.refreshGrid$.asObservable();
    }

    fireRefreshGridListeners(): void
    {
        this.refreshGrid$.next();
    }

    private readonly fetchTable$ = new Subject<void>();

    get onFetchTable(): Observable<void>
    {
        return this.fetchTable$.asObservable();
    }

    fireFetchTable(): void
    {
        this.fetchTable$.next();
    }

    private readonly updateGridSelection$ = new Subject<GridSelectionEventArg>();

    get onUpdateGridSelection(): Observable<GridSelectionEventArg>
    {
        return this.updateGridSelection$.asObservable();
    }

    fireUpdateGridSelectionListeners(value: GridSelectionEventArg): void
    {
        this.updateGridSelection$.next(value);
    }

    private readonly gridEditViewLoading$ = new Subject<boolean>();

    get onGridEditViewLoading(): Observable<boolean>
    {
        return this.gridEditViewLoading$.asObservable();
    }

    fireGridEditViewLoadingListener(value: boolean): void
    {
        this.gridEditViewLoading$.next(value);
    }

    private readonly gridResize$ = new Subject<string | null>();

    get onGridResize(): Observable<string | null>
    {
        return this.gridResize$.asObservable();
    }

    fireGridResizeListener(providerId: string = null): void
    {
        this.gridResize$.next(providerId);
    }

    private readonly cellEditOnAdd$ = new Subject<string>();

    get onCellEditOnAdd(): Observable<string>
    {
        return this.cellEditOnAdd$.asObservable();
    }

    fireCellEditOnAddListener(providerId: string): void
    {
        this.cellEditOnAdd$.next(providerId);
    }
}
